import type { FormFetcher, FormField } from '@sitecore-jss/sitecore-jss-forms';
import { createFetchBasedFormFetcher } from '@sitecore-jss/sitecore-jss-forms';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Form } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import { sitecoreApiKey } from 'temp/config';

import { FormSubmitContext } from './Context/FormSubmitContext';
import { FormElementFactory } from './CustomFieldsFactory';
import { FormErrorSummary } from './FormErrorSummary';
import { handleFormMessageEvents } from './Functions';
import { JssNextFormProps } from './Types';

import styles from './JssNextForm.module.scss';

const cx = classNames.bind(styles);

const JssNextForm = (props: JssNextFormProps): React.JSX.Element => {
  const { fields, className } = props;

  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();

  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState(0);
  const [focusRequestId, setFocusRequestId] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const formFields = fields.fields as FormField[];

  // Requests focus for the first invalid element
  const sendFocusRequest = useCallback(() => setFocusRequestId((previous) => previous + 1), []);

  const baseFetcher = useMemo(() => createFetchBasedFormFetcher(), []);
  const formFetcher: FormFetcher = useCallback(
    async (formData, endpoint) => {
      setSubmitting(true);

      try {
        const result = await baseFetcher(formData, endpoint);
        if (!(result.success && result.redirectUrl)) {
          setSubmitting(false);
        }

        const errors = result.errors ?? [];
        const validationErrors = result.validationErrors ?? {};

        if (Object.keys(validationErrors).length || errors.length) {
          sendFocusRequest();
        }

        return result;
      } catch (error) {
        setSubmitting(false);
        throw error;
      }
    },
    [baseFetcher, sendFocusRequest]
  );

  const handleRedirectAndSave = (url: string) => {
    let destUrl = url.toLowerCase();

    if (destUrl.startsWith('/en/')) {
      destUrl = destUrl.replace(/^\/en\//, '/');
    }

    try {
      if (containerRef.current) {
        const formElement = containerRef.current.querySelector('form');

        if (formElement) {
          const cleanData: Record<string, string> = {};

          const elements = formElement.querySelectorAll('input, select, textarea');

          elements.forEach((element) => {
            const el = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

            if (el.disabled) return;

            let friendlyKey: string | null = null;

            el.classList.forEach((className) => {
              if (className.startsWith('formdata-')) {
                friendlyKey = className;
              }
            });

            if (!friendlyKey) return;

            if (el instanceof HTMLSelectElement) {
              const selectedOptions = Array.from(el.selectedOptions);
              const values = selectedOptions.map((opt) => opt.value.trim()).filter((val) => val !== ''); // Ignore empty placeholders

              if (values.length > 0) {
                cleanData[friendlyKey] = values.join(', ');
              }
            } else if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
              if (el.checked) {
                if (cleanData[friendlyKey]) {
                  cleanData[friendlyKey] += `, ${el.value}`;
                } else {
                  cleanData[friendlyKey] = el.value;
                }
              }
            } else {
              const value = el.value.trim();
              if (value !== '') {
                cleanData[friendlyKey] = value;
              }
            }
          });

          sessionStorage.setItem('postSubmitFormData', JSON.stringify(cleanData));
        }
      }
    } catch (error) {
      console.error('Failed to parse form field data:', error);
    }

    router.push(destUrl);
  };

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) {
      return;
    }

    const handleSubmitButtonClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const submitButton = target?.closest('button[type="submit"]');

      // Return if click didn't originate from submit button
      if (!submitButton) {
        return;
      }

      const hasErrors = Array.from(container.querySelectorAll('.invalid')).some((element) => {
        // Ensure browser supports `checkVisibility`
        if (typeof element.checkVisibility === 'function') {
          // If it does, check if element is visible
          return element?.checkVisibility();
        }

        // If it doesn't, check if error text is not empty
        return !!element.textContent?.trim();
      });

      if (hasErrors) {
        e.preventDefault();
        sendFocusRequest();
      }
    };

    container.addEventListener('click', handleSubmitButtonClick);
    return () => container.removeEventListener('click', handleSubmitButtonClick);
  }, [sendFocusRequest]);

  useEffect(() => {
    const onEventMessage = (event: MessageEvent) => handleFormMessageEvents(event, sitecoreContext);
    window.addEventListener('message', onEventMessage);

    return () => window.removeEventListener('message', onEventMessage);
  }, [sitecoreContext]);

  // Sitecore FORM renders input and errors in a way that it doesn't add error class to the input element.
  // This useEffect is to observe the changes in the DOM and add error class to the input element when there is an error.
  // This will help us to add error styles to the input element when there is an error (e.g. red border)
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const invalidErrorSitecoreClass = 'invalid';

    const observer = new MutationObserver(() => {
      // Find all inputs inside container

      let totalErrors = 0;
      const inputTags = 'input, select, textarea';
      const inputs = containerRef.current?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        inputTags
      );

      inputs &&
        inputs.forEach((input) => {
          let nextEl = input.nextElementSibling;

          // Handle special case for radio buttons with labels
          if (!nextEl && input.labels?.length) {
            nextEl = input.labels[0].nextElementSibling;
          }

          if (nextEl && nextEl.tagName === 'DIV' && nextEl.classList.contains(invalidErrorSitecoreClass)) {
            input.classList.add(invalidErrorSitecoreClass);
            input.classList.add(`${invalidErrorSitecoreClass}-input`);
            totalErrors++;
          } else {
            input.classList.remove(invalidErrorSitecoreClass);
            input.classList.remove(`${invalidErrorSitecoreClass}-input`);
          }
        });

      // Focus and scrolling is handled through the `FormErrorSummary` component
      setFormErrors(totalErrors);
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const FixCSS = (formFields: FormField[]) => {
      if (formFields?.length === 0) {
        return;
      }

      formFields.forEach((field) => {
        const cssClass: string = (field.model['cssClass'] as string) || '';

        if (field.fields) {
          FixCSS(field.fields as FormField[]);
        }

        if (cssClass !== '') {
          const trimmedCssClass = cssClass?.trim();
          field.model['cssClass'] = cx(trimmedCssClass);
        }
      });
    };

    FixCSS(formFields);
  }, [formFields]);

  const containerClassName = cx('form-wrapper', 'component-container-edge', `form-errors-${formErrors}`, className);
  const submitContext = useMemo(() => ({ focusRequestId, submitting }), [focusRequestId, submitting]);

  return (
    <FormSubmitContext value={submitContext}>
      <div className={containerClassName} ref={containerRef}>
        <Form
          language={sitecoreContext.language || 'en'}
          errorComponent={FormErrorSummary}
          form={fields}
          fieldFactory={FormElementFactory}
          formFetcher={formFetcher}
          sitecoreApiHost={''}
          sitecoreApiKey={sitecoreApiKey}
          onRedirect={handleRedirectAndSave}
        />
      </div>
    </FormSubmitContext>
  );
};

// export default withRouter(JssNextForm);
export default compose<JssNextFormProps>(withValidDatasourceCheck(), withStyles())(JssNextForm);
